import "./Chatbot.scss";

import { Col, View } from "@amzn/stencil-react-components/layout";
import { trackStructEvent } from "@snowplow/browser-tracker";
import { useContext, useEffect, useRef } from "react";
import { useIntl } from "react-intl";

import { useChatbot } from "@/hooks/useChatbot";
import { AppContext } from "@/stores/appStore";
import { Day1EventAction, Day1EventCategory } from "@/types/snowplow-events";

import { ChatComposer } from "./ChatComposer";
import { ChatPanel } from "./ChatPanel";
import {
  AivaMessage,
  MessageBubble,
  ThinkingIndicator,
} from "./ChatTranscript";
import { NeedMoreHelp } from "./NeedMoreHelp";
import { SuggestedPrompt, SuggestedPrompts } from "./SuggestedPrompts";

/** i18n id for the Aiva panel title. */
export const CHATBOT_TITLE_ID = "chatbot-flyout-title";
/** DOM id on the Aiva panel's title heading, used to label the dialog. */
export const CHATBOT_TITLE_ELEMENT_ID = "chatbot-panel-title";

/** How long to leave the closing message on screen before handing off to IT Support. */
const ESCALATION_HANDOFF_DELAY_MS = 5000;
/** Milliseconds the typewriter spends per word, used to time the handoff. */
const TYPEWRITER_MS_PER_WORD = 30;

const STEP_WELCOME_MESSAGES: Record<string, string> = {
  IdentityCheck: "chatbot-welcome-identity-check",
  AmazonUsername: "chatbot-welcome-amazon-username",
  InsertAndTestSecurityKey: "chatbot-welcome-insert-security-key",
  RegisterSecurityKey: "chatbot-welcome-register-security-key",
  ConnectVpn: "chatbot-welcome-connect-vpn",
  AmazonPassword: "chatbot-welcome-amazon-password",
  PreCacheADMobile: "chatbot-welcome-precache",
};

interface ChatbotProps {
  onContactSupport?: (escalationSummary: string) => void;
  /** Called when the flyout header's close button is pressed. */
  onClose?: () => void;
  /** Switches to the IT Support channel. Renders the header's back button. */
  onBackToSupport?: () => void;
}

export const Chatbot = ({
  onContactSupport,
  onClose,
  onBackToSupport,
}: ChatbotProps): JSX.Element => {
  const {
    messages,
    isLoading,
    sendMessage,
    escalationSummary,
    escalationInitiated,
  } = useChatbot();
  const { state } = useContext(AppContext);
  const intl = useIntl();
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToLatest = () =>
    messagesEndRef.current?.scrollIntoView?.({ behavior: "smooth" });

  useEffect(scrollToLatest, [messages, isLoading]);

  // When the agent decides to escalate, let its closing message finish playing
  // before handing off to IT Support.
  useEffect(() => {
    if (!escalationInitiated || !onContactSupport) return;
    const lastMessage = messages[messages.length - 1];
    const typewriterDuration =
      lastMessage?.role === "assistant"
        ? lastMessage.content.split(/(\s+)/).length * TYPEWRITER_MS_PER_WORD
        : 0;
    const timer = setTimeout(
      () => onContactSupport(escalationSummary),
      typewriterDuration + ESCALATION_HANDOFF_DELAY_MS
    );
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [escalationInitiated]);

  const handleSuggestionClick = (topic: string) => {
    trackStructEvent({
      category: Day1EventCategory.Chatbot,
      action: Day1EventAction.ChatbotSuggestedTopicClicked,
      property: topic,
    });
    void sendMessage(topic);
  };

  const handleEscalate = () => {
    if (!onContactSupport) return;
    trackStructEvent({
      category: Day1EventCategory.Chatbot,
      action: Day1EventAction.ChatbotEscalationClicked,
    });
    // Fire-and-forget: record the intent in the conversation, but don't wait on
    // it — hand off to IT Support immediately.
    void sendMessage(
      intl.formatMessage({ id: "chatbot-contact-support-message" })
    );
    onContactSupport(escalationSummary);
  };

  // Follow-ups belong to the newest answer, so they are dropped while a reply is
  // still streaming. Escalation is not a follow-up — it lives in the
  // "Need more help?" box below the transcript instead.
  const lastMessage = messages[messages.length - 1];
  const followUps =
    !isLoading && lastMessage?.role === "assistant"
      ? lastMessage.suggestedTopics ?? []
      : [];

  const prompts: SuggestedPrompt[] = followUps.map((topic) => ({
    label: topic,
    onSelect: () => handleSuggestionClick(topic),
    dataTestId: "chatbotSuggestionChip",
  }));

  /*
    Escalation is only offered once Aiva has actually answered something. On the
    welcome screen, or while the first reply is still streaming, a prompt to go
    talk to a human would be pushing the user away before the bot has had a turn.
  */
  const hasAivaReplied = messages.some((msg) => msg.role === "assistant");

  // Prompts and composer go in the flyout footer so they stay pinned below the
  // scrolling transcript and, critically, sit outside the aria-live log —
  // streaming updates must never re-announce interactive content.
  const footer = (
    <Col key="chatbotFooter" width="100%" gridGap="S200">
      {prompts.length > 0 && <SuggestedPrompts prompts={prompts} />}
      <ChatComposer
        onSend={(message) => void sendMessage(message)}
        disabled={isLoading}
      />
    </Col>
  );

  const welcomeMessageId =
    STEP_WELCOME_MESSAGES[state.currentStep?.id ?? ""] ??
    "chatbot-welcome-default";

  /*
    `ChatPanel` supplies the chrome shared with the IT Support view, so the two
    headers stay identical. The transcript scrolls inside the panel body, with no
    container around the conversation as a whole — only individual Aiva responses
    carry a border.
  */
  return (
    <ChatPanel
      titleId={CHATBOT_TITLE_ID}
      titleElementId={CHATBOT_TITLE_ELEMENT_ID}
      onClose={onClose}
      backAction={
        onBackToSupport && {
          labelId: "chatbot-back-to-support",
          onClick: onBackToSupport,
          dataTestId: "chatbotBackToSupport",
        }
      }
      footer={footer}
      dataTestId="chatbotContainer"
    >
      <View
        role="log"
        aria-label={intl.formatMessage({ id: "chatbot-messages-label" })}
        aria-live="polite"
      >
        {messages.length === 0 && (
          <AivaMessage>
            {intl.formatMessage({ id: welcomeMessageId })}
          </AivaMessage>
        )}
        {messages.map((msg, i) => (
          <MessageBubble
            key={msg.id}
            msg={msg}
            animate={msg.role === "assistant" && i === messages.length - 1}
            onTick={scrollToLatest}
          />
        ))}
        {isLoading && <ThinkingIndicator />}
      </View>

      {/*
        Sits after the last turn but outside the log above, so streaming replies
        never re-announce this button.
      */}
      {onContactSupport && hasAivaReplied && (
        <NeedMoreHelp onChat={handleEscalate} />
      )}
      <div ref={messagesEndRef} />
    </ChatPanel>
  );
};
